import React from 'react';
import { Box, Typography, Grid, Paper, Divider, useMediaQuery, useTheme } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import { useLocation, useNavigate } from 'react-router-dom';
import { formatDate, formatConsumption } from '../utils/format';

const SearchResultsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { results = [], notFound = false } = location.state || {};
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down('sm')); // <600px

  // No results / not found display
  if (notFound || results.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', mt: isSm ? 3 : 6, px: isSm ? 2 : 0 }}>
        <SearchOffIcon sx={{ fontSize: isSm ? 60 : 80, color: theme.palette.text.disabled }} />
        <Typography
          variant={isSm ? 'h6' : 'h5'}
          color="textSecondary"
          mt={2}
        >
          No matching devices found
        </Typography>
        <Typography
          variant={isSm ? 'body2' : 'subtitle1'}
          color="textSecondary"
          mt={1}
        >
          Please check your search term or adjust the radius.
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={isSm ? 1 : 2}>
      <Typography variant="h5" gutterBottom>
        Search Results
      </Typography>
      {results.map(device => {
        const isGateway = Boolean(device.imei);
        const id = device._id.$oid || device._id;
        const statusColor = isGateway
          ? (device.isOnline ? 'green' : 'gray')
          : (device.criticalState ? 'red' : device.deviceStatus === 'Up' ? 'green' : 'gray');

        return (
          <Paper
            key={id}
            sx={{ mb: isSm ? 1 : 2, p: isSm ? 1 : 2, cursor: 'pointer' }}
            onClick={() => navigate(isGateway ? `/gateways/${id}` : `/water-meters/${id}`)}
          >
            {/* Responsive header */}
            {isSm ? (
              <Grid container direction="column" alignItems="center" spacing={1}>
                <Grid item>
                  <Grid container direction="row" alignItems="center" spacing={1}>
                    <Grid item>
                      <Typography variant="body1" align="center">
                        {device.deviceName}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <FiberManualRecordIcon sx={{ color: statusColor }} fontSize="small" />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item>
                  <Typography variant="caption" color="textSecondary" align="center">
                    {isGateway
                      ? `Gateway • IMEI: ${device.imei}`
                      : 'Water Meter'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2" align="center">
                    BLE Address: {device.bleAddress}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              <Grid container direction="row" alignItems="center" spacing={1}>
                <Grid item>
                  <FiberManualRecordIcon sx={{ color: statusColor }} fontSize="small" />
                </Grid>
                <Grid item xs>
                  <Typography variant="subtitle1">
                    {device.deviceName}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {isGateway
                      ? `Gateway • IMEI: ${device.imei}`
                      : 'Water Meter'}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    BLE Address: {device.bleAddress}
                  </Typography>
                </Grid>
              </Grid>
            )}

            <Divider sx={{ my: isSm ? 1 : 2 }} />

            <Grid container direction={isSm ? 'column' : 'row'} spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="body2">
                  Last Seen: {device.lastseen ? formatDate(device.lastseen, true) : 'Never'}
                </Typography>
              </Grid>
              {isGateway ? (
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2">
                    Water Meters: {device.waterMetersCount}
                  </Typography>
                </Grid>
              ) : (
                <>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      Consumption: {formatConsumption(device.totalConsumption)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2">
                      Critical: {device.criticalState ? 'Yes' : 'No'}
                    </Typography>
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>
        );
      })}
    </Box>
  );
};

export default SearchResultsPage;
